import {
  hasHttpAttachments,
  isAttachedHttpMetadata,
  isRecord,
  responseBodyToText,
} from './httpErrorGuards';
import { parseAzureError, ParsedAzureError } from './parseAzureError';

/**
 * Comprehensive error information extracted from API responses
 */
export interface ExtractedApiError {
  error: Error;
  status?: number;
  statusText?: string;
  responseBody?: unknown;
  responseText?: string;
  headers?: Record<string, string>;
  azureError?: ParsedAzureError;
  url?: string;
  method?: string;
}

const applyAttachedHttpMetadata = (
  extracted: ExtractedApiError,
  metadata: { status?: number; statusText?: string },
): void => {
  if (metadata.status !== undefined) {
    extracted.status = metadata.status;
  }
  if (metadata.statusText !== undefined) {
    extracted.statusText = metadata.statusText;
  }
};

const applyResponseBody = (
  extracted: ExtractedApiError,
  body: unknown,
): void => {
  extracted.responseBody = body;
  const text = responseBodyToText(body);
  if (text !== undefined) {
    extracted.responseText = text;
  }
};

/**
 * Extracts comprehensive error information from a fetch Response object.
 *
 * @param response - The fetch Response object (may be in error state)
 * @param url - Optional URL that was requested
 * @param method - Optional HTTP method used
 * @returns Promise resolving to ExtractedApiError with all available information
 */
export const extractApiErrorFromResponse = async (
  response: Response,
  url?: string,
  method?: string,
): Promise<ExtractedApiError> => {
  const error: ExtractedApiError = {
    error: new Error(
      `HTTP ${response.status}: ${response.statusText || 'Unknown error'}`,
    ),
    status: response.status,
    statusText: response.statusText,
    url,
    method,
  };

  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  error.headers = headers;

  try {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      try {
        const json = await response.clone().json();
        error.responseBody = json;
        error.responseText = JSON.stringify(json, null, 2);
      } catch {
        const text = await response.clone().text();
        error.responseText = text;
        error.responseBody = text;
      }
    } else {
      const text = await response.clone().text();
      error.responseText = text;
      error.responseBody = text;
    }

    if (error.responseText) {
      const azureError = parseAzureError(error.responseText);
      if (azureError.isAzureError) {
        error.azureError = azureError;
        error.error = new Error(
          `Azure Error: ${azureError.errorCode || 'Unknown'} - ${azureError.statusText || azureError.message || 'Service request failed'}`,
        );
      }
    }
  } catch (extractError) {
    console.warn('Failed to extract response body:', extractError);
  }

  return error;
};

/**
 * Extracts error information from a caught error (may be from fetch or other sources).
 *
 * @param caughtError - The error that was caught
 * @param response - Optional Response object if available
 * @param url - Optional URL that was requested
 * @param method - Optional HTTP method used
 * @returns Promise resolving to ExtractedApiError
 */
export const extractApiError = async (
  caughtError: unknown,
  response?: Response,
  url?: string,
  method?: string,
): Promise<ExtractedApiError> => {
  if (response) {
    return extractApiErrorFromResponse(response, url, method);
  }

  const error =
    caughtError instanceof Error ? caughtError : new Error(String(caughtError));

  const extracted: ExtractedApiError = {
    error,
    url,
    method,
  };

  if (hasHttpAttachments(error)) {
    const attachedResponse = error.response;

    if (attachedResponse instanceof Response) {
      return extractApiErrorFromResponse(attachedResponse, url, method);
    }

    if (attachedResponse !== undefined) {
      applyResponseBody(extracted, attachedResponse);
      if (isAttachedHttpMetadata(attachedResponse)) {
        applyAttachedHttpMetadata(extracted, attachedResponse);
      }
    }

    if (error.body !== undefined) {
      applyResponseBody(extracted, error.body);
    }
  }

  const errorText = error.message + (error.stack ? '\n' + error.stack : '');
  if (extracted.responseText) {
    const azureError = parseAzureError(extracted.responseText);
    if (azureError.isAzureError) {
      extracted.azureError = azureError;
    }
  } else {
    const azureError = parseAzureError(errorText);
    if (azureError.isAzureError) {
      extracted.azureError = azureError;
    }
  }

  return extracted;
};

const getMessageFromResponseBody = (responseBody: unknown): string | undefined => {
  try {
    const body =
      typeof responseBody === 'string'
        ? (JSON.parse(responseBody) as unknown)
        : responseBody;

    if (!isRecord(body)) {
      return undefined;
    }

    const message = body.message;
    if (typeof message === 'string') {
      return message;
    }

    const nestedError = body.error;
    if (isRecord(nestedError) && typeof nestedError.message === 'string') {
      return nestedError.message;
    }

    const errorMessage = body.errorMessage;
    if (typeof errorMessage === 'string') {
      return errorMessage;
    }
  } catch {
    // If parsing fails, continue to default
  }

  return undefined;
};

/**
 * Creates a customer-facing error message from extracted API error.
 *
 * @param extractedError - The extracted API error
 * @param defaultMessage - Default message if no specific message can be created
 * @returns User-friendly error message
 */
export const createCustomerFacingMessage = (
  extractedError: ExtractedApiError,
  defaultMessage = 'An error occurred. Please try again.',
): string => {
  if (extractedError.azureError) {
    const azure = extractedError.azureError;

    if (azure.errorCode === 'BlobNotFound') {
      return 'The requested file could not be found. It may have been moved or deleted.';
    }

    if (azure.status === 404) {
      return 'The requested resource was not found.';
    }

    if (azure.status === 403) {
      return 'You do not have permission to perform this action.';
    }

    if (azure.status === 401) {
      return 'Your session has expired. Please refresh the page and try again.';
    }

    if (azure.status === 500 || azure.status === 502 || azure.status === 503) {
      return 'The service is temporarily unavailable. Please try again in a few moments.';
    }

    if (azure.statusText) {
      return `Error: ${azure.statusText}`;
    }

    if (azure.errorCode) {
      return `Error: ${azure.errorCode}`;
    }
  }

  if (extractedError.status) {
    switch (extractedError.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Your session has expired. Please refresh the page and try again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'A conflict occurred. The resource may have been modified by another user.';
      case 413:
        return 'The file is too large. Please try a smaller file.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
        return 'The service is temporarily unavailable. Please try again in a few moments.';
      default:
        return `An error occurred (${extractedError.status}). Please try again.`;
    }
  }

  if (extractedError.responseBody) {
    const bodyMessage = getMessageFromResponseBody(extractedError.responseBody);
    if (bodyMessage) {
      return bodyMessage;
    }
  }

  const errorMessage = extractedError.error.message;
  if (
    errorMessage &&
    !errorMessage.includes('HTTP error') &&
    !errorMessage.includes('Failed to')
  ) {
    return errorMessage;
  }

  return defaultMessage;
};
